<!--
  DefaultPlanHistory.vue
  콜봇 월 정산 · 기본 요율 화면(화면 명세 3)의 버전 고르기와 고른 버전 카드(2026-10-27 목업 Rates.dc.html).

  요청: ~/md/front/admin-콜봇-월정산-프론트요청.md "화면 명세 3", "요청 계약 3-1·3-3", 트레이드오프 "이미 확정된 달은
        요율을 고쳐도 바뀌지 않는다"
  - 버전 탭: 적용 시작 월 오름차순(왼쪽이 이르다). 탭마다 월 · 적용 중(초록)/예정(파랑) 배지 · 쓰이는 범위('9월만',
    '10월부터 계속'), 지난 버전은 '지난 버전 · …'. 처음에는 이번 달에 적용 중인 버전(없으면 가장 이른 예정 버전)을 고른다.
    고른 버전은 v-model:selected 로 부모와 나눈다 — 저장 뒤 부모가 저장한 버전을 고른다. 고른 버전이 지워지면 적용 중으로 돌아간다.
    ←/→ 로 탭을 옮긴다.
  - 고른 버전 카드: '{월} 버전' 제목, 한 줄 정보(수정 시각 · 사람 · 이 버전으로 확정한 정산 n건, 툴팁에 만든 시각),
    파란 한 줄 '{직전 버전} 버전과 다른 점: …'(같으면 '같은 값', 바뀐 칸을 모두 적는다 — '외 n칸'으로 줄이지 않는다. 상담사 번호 블록이 생기거나 없어지면
    '상담사 번호 요금 시작/없앰' 한 마디로), 메모는 제목 옆 작은 '메모' 알약의 툴팁(다른 점 줄과 겹치기 쉬워 본문에 두 번 쓰지
    않는다), [수정]·[삭제], 늘 펼친 '수정 이력 n건'(건마다 바꾼 칸 전부), 그 아래 요율 표
    (SettlementRateCard mode 'plan' bare — v70 이전 모양 버전은 표 대신 안내).
  - 칸은 요금표 v70(~/md/front/admin-콜봇-월정산-call-api-구현회신.md "2026-10-16 개정" 1). v70 이전 모양으로 저장된
    버전(`legacy: true`)은 탭에 'v70 이전' 배지를 달고, 그런 버전과는 "다른 점"을 견주지 않는다. 예전 수정 이력의 칸 경로
    (`ai.price_per_unit` 등)는 "(v70 이전)"을 붙여 읽는다.
  - [수정] → emit('edit', plan). 편집 모달은 부모(SettlementRates.vue)가 연다.
  - [삭제] → 확인창 → DELETE .../default-plans/:effectiveFrom → emit('changed'). 확정 정산이 쓴 버전과 마지막 남은
    버전은 버튼 비활성 + 툴팁. 409 PLAN_IN_USE · DEFAULT_PLAN_REQUIRED, 404 PLAN_NOT_FOUND 는 안내한 뒤 다시 불러온다.
  - 버전이 하나도 없으면 아무것도 그리지 않는다(부모가 "버전 없음" 안내를 보인다).

  데이터: 버전 목록은 부모가 GET /api/v1/callbot/billing/default-plans 로 받아 내려 준다(items, cost_rates).
          삭제만 여기서 부른다.
-->
<template>
  <div v-if="rows.length" class="plan-versions">
    <div role="tablist" aria-label="기본 요율 버전" class="plan-versions__tabs">
      <button
        v-for="(row, index) in tabRows"
        :id="`${uid}-tab-${row.key}`"
        :key="row.key"
        :ref="(el) => (tabRefs[index] = el)"
        type="button"
        role="tab"
        class="plan-tab"
        :class="[`is-${row.phase}`, { 'is-selected': row.key === activeKey }]"
        :aria-selected="row.key === activeKey ? 'true' : 'false'"
        :aria-controls="panelId"
        :tabindex="row.key === activeKey ? 0 : -1"
        @click="select(row.key)"
        @keydown="onTabKey($event, index)"
      >
        <span class="plan-tab__month">
          {{ row.from }}
          <span v-if="row.phase !== 'past'" class="plan-tab__phase" :class="`is-${row.phase}`">{{ row.phaseLabel }}</span>
          <span v-if="row.legacy" class="plan-tab__phase is-legacy">v70 이전</span>
        </span>
        <span class="plan-tab__range">{{ row.tabRange }}</span>
      </button>
    </div>

    <section
      v-if="active"
      :id="panelId"
      role="tabpanel"
      class="plan-version"
      :aria-labelledby="`${uid}-tab-${active.key}`"
    >
      <div class="plan-version__head">
        <div class="plan-version__main">
          <h2 class="plan-version__title">
            {{ active.from }} 버전
            <!-- The memo often repeats the diff line below — keep it one hover/focus away -->
            <a-tooltip v-if="active.memo" :title="`메모 · ${active.memo}`" :trigger="['hover', 'focus']">
              <button type="button" class="plan-version__memo-btn">
                메모
                <span class="plan-version__sr">: {{ active.memo }}</span>
              </button>
            </a-tooltip>
          </h2>
          <p class="plan-version__meta" :title="active.createdText || undefined">{{ active.metaText }}</p>

          <p v-if="active.diff" class="plan-version__diff">
            <template v-if="active.diff.note">{{ active.diff.note }}</template>
            <template v-else-if="active.diff.items.length">
              {{ active.diff.olderFrom }} 버전과 다른 점:
              <b>{{ active.diff.allText }}</b>
            </template>
            <template v-else>{{ active.diff.olderFrom }} 버전과 같은 값입니다</template>
          </p>

        </div>

        <div class="plan-version__actions">
          <a-button @click="emit('edit', active.plan)">수정</a-button>
          <a-tooltip :title="active.deleteBlock || undefined">
            <a-button
              danger
              :disabled="!!active.deleteBlock || (!!deleting && deleting !== active.key)"
              :loading="deleting === active.key"
              @click="confirmDelete(active)"
            >
              삭제
            </a-button>
          </a-tooltip>
        </div>
      </div>

      <div class="plan-version__revisions">
        <template v-if="active.revisions.length">
          <h3 class="plan-version__rev-title">수정 이력 {{ formatNumber(active.revisions.length) }}건</h3>
          <ol class="plan-version__rev-list">
            <li v-for="rev in active.revisions" :key="rev.key" class="plan-version__rev">
              <span class="plan-version__rev-at">{{ rev.at }}</span>
              <span class="plan-version__rev-by">{{ rev.by }}</span>
              <span class="plan-version__rev-cells">바꾼 칸 · {{ rev.cells }}</span>
              <span v-if="rev.memo" class="plan-version__rev-memo">{{ rev.memo }}</span>
            </li>
          </ol>
        </template>
        <span v-else class="plan-version__no-revisions">수정 이력 없음</span>
      </div>

      <SettlementRateCard mode="plan" bare :rates="active.plan" :cost-rates="costRates" />
    </section>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, nextTick, ref } from 'vue';
import { Modal, message } from 'ant-design-vue';

import SettlementRateCard from './SettlementRateCard.vue';
import {
  addMonths,
  compareMonth,
  currentMonthKst,
  formatKstDateTime,
  formatNumber,
  isNumber,
  isValidMonth,
} from '../service/settlementFormat';
import {
  EXTRA_ITEMS,
  RATE_CELLS,
  actorLabel,
  counselorNumberRates,
  defaultPlanDeleteBlockReason,
  defaultPlanDeleteSeedNote,
  formatRateValue,
  isLegacyPlan,
  legacyCellLabel,
  planToFormValues,
  readCell,
  readCellCost,
} from '../service/settlementRules';
import { getCallApiErrorBody, getCallApiErrorCode, parseCallApiError } from '@/views/Callbot/service/callApiError';

const props = defineProps({
  /** getSettlementApi() — 삭제에만 쓴다 */
  api: { type: Object, required: true },
  /** 기본 요율 버전 전부(GET .../default-plans 의 items) */
  plans: { type: Array, default: () => [] },
  /** cost_rates — 표의 원가와 단위 초(버전에 원가 스냅샷이 없을 때) */
  costRates: { type: Object, default: null },
  /** 적용 중·예정을 가르는 기준 달(기본: 지금의 KST 달) */
  currentMonth: { type: String, default: () => currentMonthKst() },
  /** 고른 버전의 적용 시작 월(v-model:selected). 비었거나 없는 버전이면 적용 중인 버전 */
  selected: { type: String, default: '' },
});

const emit = defineEmits(['edit', 'changed', 'update:selected']);

const uid = `st-plan-versions-${getCurrentInstance()?.uid ?? 0}`;
const panelId = `${uid}-panel`;

const PHASE_LABELS = { upcoming: '예정', current: '적용 중', past: '지난 버전' };

const asArray = (v) => (Array.isArray(v) ? v : []);

/** 기본 요율에서 고치는 칸(구분 internal 은 false 고정이라 뺀다) */
const EDITABLE_CELLS = RATE_CELLS.filter((c) => c.defaultEditable);
const isCounselorCell = (cell) => cell.group === 'counselor_number';

/**
 * 수정 이력의 `changed_cells`(칸 경로) → 칸 이름. 경로는 판매가 자리('base.monthly_vat_included')로 오지만 칸 key
 * ('base.monthly')로 와도 읽히게 둘 다 둔다. 한 블록에 칸이 둘인 자리(`base`·`outbound`)가 있어 블록 경로로는 읽지 않는다.
 * 예전 이력의 v70 이전 칸('ai.price_per_unit' 등)은 legacyCellLabel 에 "(v70 이전)"을 붙이고, 모르는 경로는 그대로 보인다.
 */
const CELL_PATH_LABELS = (() => {
  const map = new Map();
  for (const cell of RATE_CELLS) {
    map.set(cell.pricePath.join('.'), cell.label);
    map.set(cell.key, cell.label);
  }
  for (const item of EXTRA_ITEMS) {
    map.set(`extras.${item.pricePath.join('.')}`, item.label);
    map.set(`extras.${item.key}`, item.label);
  }
  map.set('vat_mode', '부가세');
  map.set('memo', '메모');
  map.set('rounding', '원 미만 처리');
  map.set('counselor.pbx.stt.basis', 'STT 측정 기준');
  map.set('line.070.prorate', '추가 070 회선 일할');
  map.set('line.uan.prorate', '대표번호 회선 일할');
  return map;
})();

function cellPathLabel(path) {
  const known = CELL_PATH_LABELS.get(path);
  if (known) return known;
  const legacy = legacyCellLabel(path);
  return legacy ? `${legacy}(v70 이전)` : path;
}

function changedCellsText(cells) {
  const labels = [];
  for (const path of asArray(cells)) {
    const label = cellPathLabel(String(path));
    if (!labels.includes(label)) labels.push(label);
  }
  if (!labels.length) return '바뀐 칸 없음';
  // Every changed cell is listed — never cut to '외 n칸'
  return labels.join(', ');
}

/** 버전의 수정 이력 — 최근 것부터(시각이 같으면 뒤에 쌓인 것이 더 최근이다) */
function planRevisionRows(plan) {
  return asArray(plan?.revisions)
    .map((r, index) => ({ r, index }))
    .sort((a, b) => (isNumber(b.r?.at) ? b.r.at : 0) - (isNumber(a.r?.at) ? a.r.at : 0) || b.index - a.index)
    .map(({ r, index }) => ({
      key: `${isNumber(r?.at) ? r.at : 'x'}-${index}`,
      at: formatKstDateTime(r?.at),
      by: actorLabel(r?.by, r?.by_name),
      cells: changedCellsText(r?.changed_cells),
      memo: typeof r?.memo === 'string' ? r.memo.trim() : '',
    }));
}

const perMessage = computed(() => {
  const v = props.costRates?.web_text_per_message;
  return isNumber(v) ? v : 20;
});

/** 칸 값 표기 — 단위는 버전의 원가 스냅샷, 없으면 cost_rates */
function cellValueText(plan, key) {
  const read = readCell(plan, key);
  const unitSec = read.unitSec ?? readCellCost(props.costRates, key).unitSec;
  return formatRateValue(key, read.value, unitSec, { perMessage: perMessage.value });
}

/**
 * 상담사 번호 블록이 생기거나 없어졌을 때의 한 마디 — 두 칸을 '요금 없음 → 3개'처럼 따로 적지 않는다.
 * 블록이 둘 다 있으면 null(칸끼리 견준다).
 */
function counselorBlockDiff(older, plan) {
  const before = counselorNumberRates(older);
  const after = counselorNumberRates(plan);
  if (!before && after) {
    const inc = isNumber(after.included) ? formatNumber(after.included) : '—';
    const fee = isNumber(after.monthly) ? formatNumber(after.monthly) : '—';
    return { changed: true, text: `상담사 번호 추가 요금 시작(기본 ${inc}개 포함 · 1개당 월 ${fee}원)` };
  }
  if (before && !after) return { changed: true, text: '상담사 번호 추가 요금 없앰' };
  return before ? null : { changed: false, text: '' };
}

/** 직전(더 이른) 버전과 판매가가 다른 칸 — 어느 한쪽이 v70 이전 모양이면 칸끼리 견주지 않는다 */
function diffWithOlder(older, plan) {
  if (isLegacyPlan(older) || isLegacyPlan(plan)) {
    const which = isLegacyPlan(older) ? `${older.effective_from} 버전이` : '이 버전이';
    return {
      olderFrom: older.effective_from,
      note: `${which} 요금표 v70 이전 모양이라 ${older.effective_from} 버전과 칸끼리 견주지 않습니다`,
      items: [],
      allText: '',
    };
  }
  const before = planToFormValues(older);
  const after = planToFormValues(plan);
  const block = counselorBlockDiff(older, plan);
  const items = EDITABLE_CELLS.filter((c) => !(block && isCounselorCell(c)) && before[c.key] !== after[c.key]).map(
    (c) => `${c.label} ${cellValueText(older, c.key)} → ${cellValueText(plan, c.key)}`
  );
  if (block?.changed) items.push(block.text);
  // Every differing cell is listed in the line itself — no '외 n칸' cut
  return {
    olderFrom: older.effective_from,
    note: '',
    items,
    allText: items.join(' · '),
  };
}

/** 그 버전이 쓰이는 달 범위(삭제 확인창) */
function spanText(from, to) {
  if (to === null) return `${from}부터`;
  return from === to ? from : `${from} ~ ${to}`;
}

/** 탭의 짧은 달 — 기준 달과 같은 해면 '9월', 아니면 '2025년 9월' */
function shortMonth(month) {
  const [y, m] = month.split('-');
  return y === props.currentMonth.slice(0, 4) ? `${Number(m)}월` : `${y}년 ${Number(m)}월`;
}

/** 탭의 범위 — '10월부터 계속' · '9월만' · '9월 ~ 11월' */
function tabRangeText(from, to) {
  if (to === null) return `${shortMonth(from)}부터 계속`;
  return from === to ? `${shortMonth(from)}만` : `${shortMonth(from)} ~ ${shortMonth(to)}`;
}

/** 적용 시작 월 내림차순 — 직전·다음 버전을 찾기 쉽게 */
const sortedPlans = computed(() =>
  asArray(props.plans)
    .filter((p) => isValidMonth(p?.effective_from))
    .sort((a, b) => compareMonth(b.effective_from, a.effective_from))
);

const rows = computed(() =>
  sortedPlans.value.map((plan, index, list) => {
    const newer = list[index - 1] ?? null;
    const older = list[index + 1] ?? null;
    const from = plan.effective_from;
    // 다음(더 늦은) 버전이 시작하기 전 달까지 이 버전이 쓰인다
    const to = newer ? addMonths(newer.effective_from, -1) : null;
    let phase = 'past';
    if (compareMonth(from, props.currentMonth) > 0) phase = 'upcoming';
    else if (to === null || compareMonth(to, props.currentMonth) >= 0) phase = 'current';

    const confirmed = isNumber(plan.confirmed_statements) ? plan.confirmed_statements : null;
    const updatedAt = isNumber(plan.updated_at) ? plan.updated_at : plan.created_at;
    const updatedBy = isNumber(plan.updated_at) ? plan.updated_by : plan.created_by;
    const updatedByName = isNumber(plan.updated_at) ? plan.updated_by_name : plan.created_by_name;
    const range = tabRangeText(from, to);

    const meta = [];
    if (isNumber(updatedAt)) meta.push(`${formatKstDateTime(updatedAt)} ${actorLabel(updatedBy, updatedByName)} 수정`);
    meta.push(`이 버전으로 확정한 정산 ${confirmed === null ? '—' : `${formatNumber(confirmed)}건`}`);

    return {
      key: from,
      plan,
      from,
      to,
      olderFrom: older?.effective_from ?? null,
      phase,
      phaseLabel: PHASE_LABELS[phase],
      tabRange: phase === 'past' ? `${PHASE_LABELS.past} · ${range}` : range,
      legacy: isLegacyPlan(plan),
      metaText: meta.join(' · '),
      createdText: isNumber(plan.created_at)
        ? `만듦 ${formatKstDateTime(plan.created_at)} · ${actorLabel(plan.created_by, plan.created_by_name)}`
        : '',
      memo: typeof plan.memo === 'string' ? plan.memo.trim() : '',
      diff: older ? diffWithOlder(older, plan) : null,
      revisions: planRevisionRows(plan),
      // 삭제를 막는 이유(버튼 비활성 + 툴팁) — 서버도 409 로 막는다
      deleteBlock: defaultPlanDeleteBlockReason(plan, list),
    };
  })
);

/** 탭은 왼쪽이 이른 달 */
const tabRows = computed(() => [...rows.value].reverse());

// ---------------------------------------------------------------------------
// 버전 고르기
// ---------------------------------------------------------------------------

/** 처음 고를 버전 — 적용 중, 없으면(모두 예정) 가장 이른 버전 */
const defaultKey = computed(() => rows.value.find((r) => r.phase === 'current')?.key ?? tabRows.value[0]?.key ?? '');

const activeKey = computed(() => (rows.value.some((r) => r.key === props.selected) ? props.selected : defaultKey.value));

const active = computed(() => rows.value.find((r) => r.key === activeKey.value) ?? null);

/** 탭 버튼(←/→ 로 옮길 때 초점) — 그리기에 쓰지 않아 반응형이 아니다 */
const tabRefs = [];

function select(key) {
  if (key !== props.selected) emit('update:selected', key);
}

function onTabKey(event, index) {
  const list = tabRows.value;
  let next = -1;
  if (event.key === 'ArrowRight') next = (index + 1) % list.length;
  else if (event.key === 'ArrowLeft') next = (index - 1 + list.length) % list.length;
  else if (event.key === 'Home') next = 0;
  else if (event.key === 'End') next = list.length - 1;
  if (next < 0) return;
  event.preventDefault();
  select(list[next].key);
  void nextTick(() => tabRefs[next]?.focus?.());
}

// ---------------------------------------------------------------------------
// 삭제
// ---------------------------------------------------------------------------

/** 삭제 중인 버전의 적용 시작 월 */
const deleting = ref('');

/** 삭제하면 그 달들이 무엇을 따르게 되는지까지 알린다 — 가장 이른 버전이면 그 달들은 기본 요율이 없어진다 */
function deleteConfirmText(row) {
  const months = row.to === null ? `${row.from}부터의 달은` : `${spanText(row.from, row.to)} 달은`;
  const seed = defaultPlanDeleteSeedNote(row.from);
  const head = [`기본 요율 ${row.from} 버전을 삭제합니다.`, seed].filter(Boolean).join(' ');
  if (row.olderFrom) {
    return `${head} ${months} ${row.olderFrom} 버전을 따르게 되고, 그중 확정 전인 달(추정 중)의 금액이 바로 바뀝니다. 계속할까요?`;
  }
  return `${head} 가장 이른 버전이라 ${months} 적용할 기본 요율이 없어져 금액을 계산하지 않고, 마감하면 검토 필요(기본 요율 없음)로 멈춥니다. 계속할까요?`;
}

function confirmDelete(row) {
  if (row.deleteBlock || deleting.value) return;
  Modal.confirm({
    title: '기본 요율 버전 삭제',
    content: deleteConfirmText(row),
    okText: '삭제',
    okType: 'danger',
    cancelText: '취소',
    onOk: () => doDelete(row.from),
  });
}

async function doDelete(effectiveFrom) {
  deleting.value = effectiveFrom;
  try {
    await props.api.deleteDefaultPlan(effectiveFrom);
    message.success(`기본 요율 ${effectiveFrom} 버전을 삭제했습니다.`);
    emit('changed');
  } catch (err) {
    const code = getCallApiErrorCode(err);
    if (code === 'PLAN_IN_USE') {
      const used = getCallApiErrorBody(err)?.error?.details?.confirmed_statements;
      const count = isNumber(used) ? ` ${formatNumber(used)}건` : '';
      message.warning(`확정 정산${count}이 쓴 버전이라 삭제할 수 없습니다. 목록을 새로 불러옵니다.`);
      emit('changed');
    } else if (code === 'DEFAULT_PLAN_REQUIRED') {
      message.warning('마지막 남은 버전은 삭제할 수 없습니다. 목록을 새로 불러옵니다.');
      emit('changed');
    } else if (code === 'PLAN_NOT_FOUND') {
      message.warning('이미 삭제된 버전입니다. 목록을 새로 불러옵니다.');
      emit('changed');
    } else {
      message.error(
        parseCallApiError(err, '기본 요율 버전을 삭제하지 못했습니다.', {
          unavailableMessage: 'call-api 에 기본 요율 API 가 아직 없습니다(배포 전).',
        })
      );
    }
  } finally {
    deleting.value = '';
  }
}
</script>

<style scoped lang="scss">
@use '../styles/settlement' as *;

.plan-versions {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }
}

// 버전 탭 — 전역 common.scss 의 `button { height: 40px }` 를 끈다
.plan-tab {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 170px;
  height: auto;
  padding: 10px 16px;
  border: 1px solid $st-border-strong;
  border-radius: 10px;
  background: #fff;
  color: $st-text;
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:hover {
    border-color: $st-text-secondary;
  }

  &.is-selected {
    padding: 9px 15px;
    border: 2px solid $st-text;
  }

  &:focus-visible {
    outline: 2px solid $st-accent;
    outline-offset: 2px;
  }

  &__month {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px 6px;
    font-size: 15px;
    font-weight: 600;
    @include st-num;
  }

  &.is-past &__month {
    color: $st-text-secondary;
  }

  &.is-selected &__month {
    font-weight: 700;
    color: $st-text;
  }

  &__phase {
    @include st-pill;

    &.is-current {
      @include st-pill-tone($st-ok, $st-ok-bg, $st-ok-border);
    }

    &.is-upcoming {
      @include st-pill-tone($st-accent, $st-accent-bg, $st-accent-border);
    }

    &.is-legacy {
      @include st-pill-tone($st-warning, $st-warning-bg, $st-warning-border);
    }
  }

  &__range {
    font-size: 12.5px;
    color: $st-muted;
  }
}

.plan-version {
  border: 1px solid $st-border-strong;
  border-radius: 12px;
  background: #fff;
  overflow: hidden;

  &__head {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px 16px;
    padding: 18px 20px 12px;
  }

  &__main {
    display: flex;
    flex: 1 1 420px;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
  }

  &__title {
    margin: 0;
    font-size: 17px;
    font-weight: 700;
    color: $st-text;
    @include st-num;
  }

  &__meta {
    margin: 0;
    font-size: 13px;
    color: $st-text-secondary;
    font-variant-numeric: tabular-nums;
  }

  // 직전 버전과 다른 점 — 한 줄 강조
  &__diff {
    margin: 0;
    padding: 8px 12px;
    border-radius: 8px;
    background: $st-accent-bg;
    font-size: 13.5px;
    color: $st-text;
    word-break: keep-all;

    b {
      font-weight: 600;
    }
  }

  &__memo-btn {
    @include st-pill;
    height: auto; // override the global button { height: 40px }
    margin-left: 6px;
    font-family: inherit;
    font-weight: 500;
    vertical-align: 3px;
    cursor: help;

    &:focus-visible {
      outline: 2px solid $st-accent-border;
      outline-offset: 1px;
    }
  }

  &__sr {
    @include st-sr-only;
  }

  &__actions {
    display: flex;
    flex: none;
    gap: 8px;
  }

  &__revisions {
    padding: 0 20px 14px;
    border-bottom: 1px solid $st-border;
  }

  &__rev-title {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    color: $st-text;
  }

  &__no-revisions {
    font-size: 12.5px;
    color: $st-muted;
  }

  &__rev-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin: 8px 0 0;
    padding: 8px 12px;
    border-radius: 8px;
    background: $st-bg;
    list-style: none;
  }

  &__rev {
    display: flex;
    flex-wrap: wrap;
    gap: 2px 0;
    font-size: 12.5px;
    color: $st-text-secondary;

    > span + span::before {
      content: '·';
      margin: 0 6px;
      color: $st-border-strong;
    }
  }

  &__rev-at {
    @include st-num;
  }

  &__rev-by {
    font-weight: 600;
    color: $st-text;
  }

  &__rev-memo {
    color: $st-text;
    word-break: break-word;
  }
}
</style>
